// src/features/practitioner/import/EditPractitionerModal.jsx
import React, { useEffect, useRef } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import BootDatePicker from "components/common/custom/forms/BootDatePicker";
import MultiSelect from "components/common/MultiSelect";
import {
  genderData,
  maritalStatusData,
  nationalityData,
  specilaityData,
} from "../utils";
import FalconCloseButton from "components/common/FalconCloseButton";
import TextInput from "components/common/custom/forms/BootTextField/TextField";
import moment from "moment";

const isEnglish = (value) => /^[A-Za-z0-9\s'-]+$/.test(value);
const isArabic = (value) => !value || /^[\u0600-\u06FF\s]+$/.test(value);
const isValidDate = (value) => value instanceof Date && !isNaN(value.getTime());

const EditPractitionerModal = ({
  show,
  onHide,
  rowData,
  updateRowData,
  setSelectedRow,
}) => {
  const {
    watch,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm();

  const allBranches = JSON.parse(localStorage.getItem("branch_settings")) || [];
  const prevRowNum = useRef(null);

  // Function to convert title to value
  const getValueFromTitle = (dataList, title) => {
    if (!title) return "";
    const normalized = title?.trim().toLowerCase();
    const match = dataList.find(
      (item) => item.title?.trim().toLowerCase() === normalized
    );
    return match ? match.value : title;
  };

  // Reset form when modal is closed
  useEffect(() => {
    if (!show) {
      reset();
      setSelectedRow(null);
      prevRowNum.current = null;
    }
  }, [show, reset, setSelectedRow]);

  // Initialize form using setValue when rowData changes
  useEffect(() => {
    if (show && rowData && prevRowNum.current !== rowData.rowNum) {
      // Convert dropdown values from titles to actual values
      const genderValue = getValueFromTitle(genderData, rowData["Gender"]);
      const nationalityValue = getValueFromTitle(
        nationalityData,
        rowData["Nationality"]
      );
      const maritalStatusValue = getValueFromTitle(
        maritalStatusData,
        rowData["Marital Status"]
      );

      // Set all values individually using setValue
      setValue("License No", rowData["License No"]);
      setValue("First Name (Eng)", rowData["First Name (Eng)"]);
      setValue("Last Name (Eng)", rowData["Last Name (Eng)"]);
      setValue("First Name (Arabic)", rowData["First Name (Arabic)"]);
      setValue("Last Name (Arabic)", rowData["Last Name (Arabic)"]);
      setValue("Marital Status", maritalStatusValue);
      setValue("Gender", genderValue);
      setValue(
        "Nationality",
        nationalityData?.find((item) => item?.value === nationalityValue)
      );
      setValue("Mobile No", rowData["Mobile No"]);
      setValue("Email", rowData["Email"]);
      setValue("National Id", rowData["National Id"]);
      setValue("Passport No", rowData["Passport No"]);
      // Extract speciality data from either 'Speciality' or 'speciality'
      const rawSpeciality = rowData["speciality"] || rowData["Speciality"];

      // Normalize to array of titles
      const specialityTitles = Array.isArray(rawSpeciality)
        ? rawSpeciality.map((s) =>
            typeof s === "string" ? s.trim() : s.label?.trim()
          )
        : typeof rawSpeciality === "string"
        ? rawSpeciality.split(",").map((s) => s.trim())
        : [];

      // Map titles to value/label pairs
      const specialityOptions = specialityTitles
        .map((title) => {
          const match = specilaityData?.find(
            (item) =>
              item?.title?.trim()?.toLowerCase() === title?.toLowerCase()
          );
          return match
            ? {
                value: match.value,
                label: match.title,
              }
            : null;
        })
        .filter(Boolean); // remove nulls

      setValue("speciality", specialityOptions);

      // Set dates
      setValue(
        "License Expiry",
        rowData["License Expiry"] ? new Date(rowData["License Expiry"]) : ""
      );
      setValue(
        "DOB",
        rowData["DOB"]
          ? moment(new Date(rowData["DOB"])).format("DD-MM-YYYY")
          : ""
      );

      // Set branch data - already uses IDs
      const branchValues = rowData.Branch
        ? rowData.Branch.split(",")
            .map((branchIdStr) => {
              const trimmedId = branchIdStr.trim();
              const branch = allBranches.find(
                (b) => b.branch_cchino === trimmedId
              );
              return branch
                ? {
                    value: branch.branch_cchino,
                    label: branch.branch_name,
                    branch_cchino: branch.branch_cchino,
                  }
                : null;
            })
            .filter(Boolean)
        : [];

      setValue("Branch", branchValues);

      // Update tracked row
      prevRowNum.current = rowData.rowNum;
    }
  }, [show, rowData, setValue, allBranches]);

  const handleFormSubmit = (data) => {
    // Transform branch data
    const formattedData = {
      ...data,
      Nationality: data?.Nationality?.value,
      "License Expiry": data?.["License Expiry"]
        ? moment(data?.["License Expiry"]).format("YYYY-MM-DD")
        : "",
      DOB: data?.["DOB"] ? moment(data?.["DOB"]).format("YYYY-MM-DD") : "",
      Branch: data.Branch.map((b) => b.value).join(","),
    };

    // Check if form has errors
    const isValid = Object.keys(errors).length === 0;

    // Update row data and validation status
    updateRowData(rowData.rowNum, formattedData, isValid);
    reset();
    onHide();
    setSelectedRow(null);
  };

  return (
    <Modal show={show} size="lg" centered>
      <Modal.Header>
        <Modal.Title>Edit Practitioner </Modal.Title>
        <FalconCloseButton
          onClick={() => {
            reset();
            onHide();
            setSelectedRow(null);
            setValue("First Name (Eng)", "");
            setValue("Last Name (Eng)", "");
          }}
          size="sm"
          className="position-absolute top-0 end-0 me-2 mt-2"
        />
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit(handleFormSubmit)}>
          <Row>
            {/* License No */}
            <Col xs={12} sm={6} md={4} xl={3}>
              <TextInput
                name="License No"
                label="License No"
                control={control}
                size="sm"
                required={true}
                errors={errors}
                rules={{
                  required: "License number is required",
                }}
              />
            </Col>

            <Col xs={12} sm={6} md={4} xl={3}>
              <BootDatePicker
                name="License Expiry"
                label="License Expiry"
                size="sm"
                control={control}
                errors={errors}
                selected={watch("License Expiry")}
                onChange={(date) => setValue("License Expiry", date)}
                placeholder="YYYY-MM-DD"
                isRequired={false}
              />
            </Col>

            {/* First Name Eng */}
            <Col xs={12} sm={6} md={4} xl={3}>
              <TextInput
                name="First Name (Eng)"
                label="First Name (English)"
                control={control}
                size="sm"
                required={true}
                errors={errors}
                rules={{
                  required: "First name is required",
                  validate: (v) =>
                    isEnglish(v) || "Only English characters are allowed",
                }}
              />
            </Col>

            {/* Last Name Eng */}
            <Col xs={12} sm={6} md={4} xl={3}>
              <TextInput
                name="Last Name (Eng)"
                label="Last Name (English)"
                control={control}
                size="sm"
                required={true}
                errors={errors}
                rules={{
                  required: "Last name is required",
                  validate: (v) =>
                    isEnglish(v) || "Only English characters are allowed",
                }}
              />
            </Col>

            {/* First Name Arabic */}
            <Col xs={12} sm={6} md={4} xl={3}>
              <TextInput
                name="First Name (Arabic)"
                label="First Name (Arabic)"
                control={control}
                size="sm"
                required={false}
                errors={errors}
                rules={{
                  validate: (v) => isArabic(v) || "Must be Arabic characters",
                }}
              />
            </Col>

            {/* Last Name Arabic */}
            <Col xs={12} sm={6} md={4} xl={3}>
              <TextInput
                name="Last Name (Arabic)"
                label="Last Name (Arabic)"
                control={control}
                size="sm"
                required={false}
                errors={errors}
                rules={{
                  validate: (v) => isArabic(v) || "Must be Arabic characters",
                }}
              />
            </Col>

            {/* Marital Status */}
            <Col xs={12} sm={6} md={4} xl={3}>
              <Controller
                control={control}
                name="Marital Status"
                render={({ field }) => (
                  <Form.Group>
                    <Form.Label>Marital Status</Form.Label>
                    <Form.Select {...field} size="sm" className="fs-10">
                      <option value="">Select Marital Status</option>
                      {maritalStatusData.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.title}
                        </option>
                      ))}
                    </Form.Select>
                    {errors["Marital Status"] && (
                      <div className="text-danger small mt-1">
                        {errors["Marital Status"].message}
                      </div>
                    )}
                  </Form.Group>
                )}
              />
            </Col>

            {/* Gender */}
            <Col xs={12} sm={6} md={4} xl={3}>
              <Controller
                control={control}
                name="Gender"
                rules={{
                  required: "Gender is required",
                  validate: (val) =>
                    genderData.some((g) => g.value === val) ||
                    "Invalid gender selected",
                }}
                render={({ field, fieldState: { error } }) => (
                  <Form.Group>
                    <Form.Label>
                      Gender <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select {...field} size="sm" className="fs-10">
                      <option value="">Select Gender</option>
                      {genderData.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.title}
                        </option>
                      ))}
                    </Form.Select>
                    {error && (
                      <div className="text-danger small mt-1">
                        {error.message}
                      </div>
                    )}
                  </Form.Group>
                )}
              />
            </Col>

            {/* Nationality */}
            <Col xs={12} sm={6} md={4} xl={3}>
              <label className="form-label mb-2">
                Nationality <span className="text-danger">*</span>
              </label>
              <Controller
                name="Nationality"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <MultiSelect
                    {...field}
                    menuPlacement="auto"
                    closeMenuOnSelect={false}
                    isMulti={false}
                    options={nationalityData?.map((item) => ({
                      value: item.value,
                      label: item.title,
                    }))}
                    isClearable={true}
                    value={
                      field.value
                        ? {
                            value: field.value.value,
                            label: field.value.title,
                          }
                        : null
                    }
                    onChange={(selected) => {
                      setValue(
                        "Nationality",
                        selected
                          ? {
                              value: selected.value,
                              title: selected.label,
                            }
                          : null
                      );
                    }}
                    formatOptionLabel={({ label }) => <div>{label}</div>}
                    getOptionValue={(option) => option.value}
                    placeholder="Choose ..."
                  />
                )}
              />
            </Col>
            <Col xs={12} sm={6} md={4} xl={3}>
              <BootDatePicker
                name="DOB"
                label="Date of Birth"
                size="sm"
                control={control}
                errors={errors}
                selected={watch("DOB")}
                onChange={(date) => setValue("DOB", date)}
                placeholder="YYYY-MM-DD"
                isRequired={false}
              />
            </Col>

            {/* Mobile No */}
            <Col xs={12} sm={6} md={4} xl={3}>
              <TextInput
                name="Mobile No"
                label="Mobile No"
                control={control}
                size="sm"
                required={false}
                errors={errors}
                placeholder="05xxxxxxxx"
                type="tel"
                inputMode="numeric"
                rules={{
                  validate: (value) => {
                    if (!value) return true; // Not required
                    return (
                      /^05\d{8}$/.test(value) ||
                      "Must start with 05 and be 10 digits"
                    );
                  },
                }}
              />
            </Col>

            {/* Email */}
            <Col xs={12} sm={6} md={4} xl={3}>
              <TextInput
                name="Email"
                label="Email"
                control={control}
                size="sm"
                required={false}
                errors={errors}
                type="email"
                rules={{
                  validate: (val) =>
                    !val ||
                    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val) ||
                    "Invalid email address",
                }}
              />
            </Col>

            {/* National ID */}
            <Col xs={12} sm={6} md={4} xl={3}>
              <TextInput
                name="National Id"
                label="National ID"
                control={control}
                size="sm"
                required={false}
                errors={errors}
                inputMode="numeric"
                rules={{
                  validate: (value) => {
                    const nationalId = String(value || "").trim();
                    const passport = String(watch("Passport No") || "").trim();
                    const nationality = watch("Nationality")?.value;

                    const hasNationalId = nationalId !== "";
                    const hasPassport = passport !== "";

                    if (nationality === "SAU") {
                      if (!hasNationalId)
                        return "National ID is required for SAU nationals";
                      if (!/^[0-9]{10}$/.test(nationalId)) {
                        return "National ID must be exactly 10 digits";
                      }
                      if (!nationalId.startsWith("1")) {
                        return "National ID must start with '1' for SAU nationals";
                      }
                      if (hasPassport)
                        return "Only one of National ID or Passport No is allowed";
                    } else {
                      if (!hasNationalId && !hasPassport)
                        return "Either National ID or Passport No is required";

                      if (hasNationalId) {
                        if (nationalId.length !== 10)
                          return "National ID must be 10 characters";
                        if (nationalId.startsWith("1"))
                          return "National ID should not start with '1' for non-SAU nationals";
                      }

                      if (hasNationalId && hasPassport)
                        return "Only one of National ID or Passport No is allowed";
                    }

                    return true;
                  },
                }}
              />
            </Col>

            {/* Passport No */}
            <Col xs={12} sm={6} md={4} xl={3}>
              <TextInput
                name="Passport No"
                label="Passport No"
                control={control}
                size="sm"
                required={false}
                errors={errors}
                inputMode="text"
                rules={{
                  validate: (value) => {
                    const passport = String(value || "").trim();
                    const nationalId = String(
                      watch("National Id") || ""
                    ).trim();
                    const nationality = watch("Nationality")?.value;

                    const hasPassport = passport !== "";
                    const hasNationalId = nationalId !== "";

                    if (nationality === "SAU") {
                      if (hasPassport)
                        return "Passport No should not be provided for SAU nationals";
                    } else {
                      if (!hasNationalId && !hasPassport)
                        return "Either Passport No or National ID is required";

                      if (hasPassport) {
                        if (!/^[A-Za-z0-9]{10}$/.test(passport)) {
                          return "Passport number must be 10 alphanumeric characters";
                        }
                      }

                      if (hasPassport && hasNationalId)
                        return "Only one of National ID or Passport No is allowed";
                    }

                    return true;
                  },
                }}
              />
            </Col>

            {/* Speciality */}
            <Col xs={12} sm={6} md={4} xl={3}>
              <label className="form-label">
                Speciality <span className="text-danger">*</span>
              </label>
              <Controller
                name="speciality"
                control={control}
                rules={{ required: "Speciality is required" }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <MultiSelect
                      {...field}
                      menuPlacement="auto"
                      closeMenuOnSelect={false}
                      isMulti
                      isClearable
                      options={specilaityData.map((item) => ({
                        value: item.value,
                        label: item.title,
                      }))}
                      value={field.value}
                      onChange={(selected) => field.onChange(selected)}
                      formatOptionLabel={({ label }) => <div>{label}</div>}
                      getOptionValue={(option) => option.value}
                      placeholder="Choose ..."
                    />
                    {error && (
                      <div className="text-danger small mt-1">
                        {error.message}
                      </div>
                    )}
                  </>
                )}
              />
            </Col>

            {/* Branch */}
            <Col xs={12} sm={6} md={4} xl={3}>
              <Form.Group>
                <label className="form-label">
                  Branches <span className="text-danger">*</span>
                </label>
                <Controller
                  name="Branch"
                  control={control}
                  rules={{
                    required: "At least one branch is required",
                    validate: (value) => value?.length > 0,
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <div>
                      <MultiSelect
                        {...field}
                        closeMenuOnSelect={false}
                        isMulti
                        options={allBranches.map((branch) => ({
                          value: branch.branch_cchino,
                          label: branch.branch_name,
                        }))}
                        value={field.value}
                        onChange={(selected) => field.onChange(selected)}
                        placeholder="Choose branches..."
                      />
                      {error && (
                        <div className="text-danger small mt-1">
                          {error.message}
                        </div>
                      )}
                    </div>
                  )}
                />
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex justify-content-end mt-4">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                reset();
                onHide();
                setSelectedRow(null);
              }}
              className="me-2"
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" variant="primary">
              Save Changes
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default EditPractitionerModal;
